import React, { useEffect, useState } from 'react';
import {
    Modal,
    Form,
    Input,
    Button,
    InputNumber,
    DatePicker,
    Select,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { fetchListings } from '../helpers/googleCloud';
import { disabledDate } from '../helpers/helpers';

const AddTaskModal = ({
    isModalVisible,
    handleFormSubmit,
    handleModalClose,
    editingTask,
    date,
    users
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [listings, setListings] = useState(null);
    const [form] = Form.useForm();

    const fetchdata = async () => {
        setIsLoading(true);

        const listings = await fetchListings();
        const modefiedListings = listings.map((listing) => {
            return {
                value: listing._id,
                label: listing.nickname,
            };
        });

        setListings(modefiedListings);
        setIsLoading(false);
    };

    useEffect(() => {
        fetchdata();
    }, []);

    useEffect(() => {
        if (!listings) {
            return;
        }

        if (editingTask) {
            form.setFieldsValue({
                title: editingTask.title,
                duration: editingTask.duration,
                deadline: editingTask.mustFinishBefore
                    ? dayjs(editingTask.mustFinishBefore)
                    : dayjs(date).hour(16).minute(0),
                description: editingTask.description,
                startTime: editingTask.startTime,
            });
        } else {
            form.setFieldsValue({
                startTime: dayjs(date).hour(10).minute(0),
                deadline: dayjs(date).hour(16).minute(0),
            });
        }
    }, [listings, editingTask, form, date]);

    const filterOption = (input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase());


    const modefiedUsers = users.map((user) => {
        return {
            value: user._id,
            label: user.fullName,
        };
    });

    return (
        <>
            <Modal
                loading={!listings || isLoading}
                footer={null}
                open={isModalVisible}
                title={editingTask ? 'Edit Task' : 'Add New Task'}
                onCancel={handleModalClose}
            >
                <Form
                    form={form}
                    key={editingTask ? editingTask.id : 'newTask'}
                    onFinish={handleFormSubmit}
                >
                    <Form.Item
                        name='title'
                        rules={[
                            {
                                required: true,
                                message: 'Please input the task title!',
                            },
                        ]}
                    >
                        <label>
                            Title
                            <Input
                                placeholder='Task Title'
                                defaultValue={
                                    editingTask ? editingTask.title : ''
                                }
                            />
                        </label>
                    </Form.Item>
                    <Form.Item name='description'>
                        <label>
                            Description
                            <Input.TextArea
                                rows={5}
                                placeholder='Task description'
                                defaultValue={
                                    editingTask ? editingTask.description : ''
                                }
                            />
                        </label>
                    </Form.Item>
                    <Form.Item
                        name='startTime'
                        rules={[
                            {
                                required: true,
                                message: 'Please select!',
                            },
                        ]}
                    >
                        <label>
                            Start time
                            <DatePicker
                                onChange={(date) =>
                                    form.setFieldValue('startTime', date)
                                }
                                showTime
                                defaultValue={
                                    editingTask
                                        ? dayjs(editingTask.startTime)
                                        : dayjs(date).hour(10).minute(0)
                                }
                                disabledDate={disabledDate}
                                format='YYYY-MM-DD HH:mm'
                                placeholder='Select start time'
                                style={{ width: '100%' }}
                            />
                        </label>
                    </Form.Item>
                    <Form.Item
                        name='deadline'
                        rules={[
                            {
                                required: true,
                                message: 'Please select!',
                            },
                        ]}
                    >
                        <label>
                            Must Finish Before
                            <DatePicker
                                showTime
                                onChange={(date) =>
                                    form.setFieldValue('deadline', date)
                                }
                                disabledDate={disabledDate}
                                defaultValue={
                                    editingTask && editingTask.mustFinishBefore
                                        ? dayjs(editingTask.mustFinishBefore)
                                        : dayjs(date).hour(16).minute(0)
                                }
                                format='YYYY-MM-DD HH:mm'
                                placeholder='Select deadline'
                                style={{ width: '100%' }}
                            />
                        </label>
                    </Form.Item>
                    <Form.Item
                        name='duration'
                        rules={[
                            {
                                required: true,
                                message: 'Please input the duration!',
                            },
                        ]}
                    >
                        <label>
                            Duration (minutes)
                            <InputNumber
                                min={1}
                                placeholder='Duration in minutes'
                                style={{ width: '100%' }}
                                defaultValue={
                                    editingTask ? editingTask.duration : ''
                                }
                            />
                        </label>
                    </Form.Item>
                    {editingTask ? null : (
                        <>
                            <Form.Item
                                name='assigneeId'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please choose the employee!',
                                    },
                                ]}
                            >
                                <label>
                                    Employee
                                    <Select
                                        options={modefiedUsers}
                                        showSearch
                                        filterOption={filterOption}
                                        onChange={(id) =>
                                            form.setFieldValue('assigneeId', id)
                                        }
                                    />
                                </label>
                            </Form.Item>
                            <Form.Item
                                name='listingId'
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            'Please choose the task hotel listing!',
                                    },
                                ]}
                            >
                                <label>
                                    Listing
                                    <Select
                                        options={listings}
                                        showSearch
                                        filterOption={filterOption}
                                        onChange={(id) =>
                                            form.setFieldValue('listingId', id)
                                        }
                                    />
                                </label>
                            </Form.Item>
                        </>
                    )}

                    <Form.Item>
                        <Button htmlType='submit' type='primary'>
                            Save
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

AddTaskModal.propTypes = {
    isModalVisible: PropTypes.bool.isRequired,
    handleFormSubmit: PropTypes.func.isRequired,
    handleModalClose: PropTypes.func.isRequired,
    editingTask: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        duration: PropTypes.string,
        mustFinishBefore: PropTypes.string,
        description: PropTypes.string,
        startTime: PropTypes.string,
    }),
    date: PropTypes.object,
    users: PropTypes.array
};

export default AddTaskModal;
